import { useMemo } from 'react';
import { ProductUsp } from '@hub/common/ProductUsp/ProductUsp';
import { DeliveryInfo } from '@hub/common/DeliveryInfo/DeliveryInfo';
import { getLocale, type DeliveryTime } from '@hub/libs/constants';
import { useTranslation } from 'next-i18next';
import { Badge } from '@hub/common/Badge/Badge';
import type { DeliveryTimeFragment } from '@hub/generated/graphql';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { formatPrice } from '@hub/libs/product-price-utils';
import { Link } from '@hub/common/Link/Link';
import { IconID } from '@hub/common/Icon/Icon';
import type { WidenMediaElement } from '@hub/libs/widen-utils';
import type { AggregatedProductPrice } from '@hub/models/enhanced-fragments';
import type { GraphQlPriceType } from '@hub/models/graphql';
import { useRouter } from 'next/router';
import { getPriceQuantityUnitLabelDeprecated } from '@hub/libs/product.helper';

export interface SuitableProductProps {
  title: string;
  slug: string;
  usp: string;
  new: boolean;
  image: WidenMediaElement | null;
  priceType: GraphQlPriceType;
  prices: AggregatedProductPrice | null;
  deliveryTimes: DeliveryTimeFragment[] | null;
  // eslint-disable-next-line react/no-unused-prop-types
  productId?: string | null;
  productIdDisplayName?: string | null;
  legacyLinkTarget?: string | null;
  primaryCategoryCode?: string;
}

export const SuitableProduct = ({
  deliveryTimes,
  image,
  new: isNew,
  priceType,
  prices,
  slug,
  title,
  usp,
  productIdDisplayName,
  legacyLinkTarget,
  primaryCategoryCode,
}: SuitableProductProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  // generate Link Url
  // include variant sku if preselected variant exists
  const currentLinkTarget = useMemo(
    () =>
      legacyLinkTarget ||
      `/product/${slug}${productIdDisplayName ? '?' : ''}${new URLSearchParams(
        productIdDisplayName ? { artnr: productIdDisplayName } : undefined
      ).toString()}`,
    [slug, productIdDisplayName, legacyLinkTarget]
  );

  const { t, i18n } = useTranslation(['common', 'category/productTeaserPriceBox', 'category/productTeaser']);
  const priceUnitDisplayLabel = getPriceQuantityUnitLabelDeprecated(locale, priceType, primaryCategoryCode);
  return (
    <div className="mb-6 pb-8 border-b last:border-b-0 flex-auto w-full">
      <div data-testid="product-teaser-loading">
        <h3 className="lg:mb-2 text-gray-500 font-bold text-sm">{title}</h3>
        <div className="flex flex-col lg:flex-row">
          <div className="flex flex-col lg:w-2/3">
            <div className="flex flex-col lg:flex-row">
              <div className="w-full sm:w-1/2 lg:w-1/3">
                {isNew && <Badge text={t('PRODUCT_OVERVIEW.NEW_PRODUCT', { ns: 'category/productTeaser' })} />}
                {image && (
                  <Link href={currentLinkTarget} flex={false}>
                    <div className="border relative">
                      <WidenImage
                        data-testid="suitable-product-image"
                        width={223}
                        height={148}
                        layout="responsive"
                        alt={image.description ?? title}
                        shortCode={image.shortCode}
                        filename={image.filename}
                      />
                    </div>
                  </Link>
                )}
              </div>
              <div className="lg:w-2/3 px-0 lg:px-6 xs:my-6 sm:my-6 lg:my-0 text-base">
                <ProductUsp removeLinks usp={usp} />
              </div>
            </div>
          </div>
          <div className="lg:w-1/3">
            <div className="bg-gray-50 p-6">
              {prices ? (
                <div>
                  {t('PRODUCT_OVERVIEW.PRICE_BOX.PRICE', { ns: 'category/productTeaserPriceBox' })}
                  {(prices && prices.regularPrice && prices.regularPrice > 0 && (
                    <span className="text-3xl block" data-testid="product-total-price">
                      {`${formatPrice(prices.regularPrice, i18n.language, prices.currency)}/${t(priceUnitDisplayLabel, {
                        ns: 'category/productTeaserPriceBox',
                      })}`}
                    </span>
                  )) || (
                    <span className="text-3xl block">
                      {t('PRODUCT_OVERVIEW.PRICE_BOX.PRICE_ON_REQUEST', { ns: 'category/productTeaserPriceBox' })}
                    </span>
                  )}
                </div>
              ) : null}

              {deliveryTimes?.[0] && deliveryTimes?.[0]?.code !== undefined && deliveryTimes?.[0]?.code !== null && (
                <div className="my-6">
                  <DeliveryInfo
                    deliveryTimeCode={deliveryTimes?.[0]?.code.toString() as DeliveryTime}
                    deliveryTimeText={deliveryTimes?.[0]?.label || undefined}
                  />
                </div>
              )}
              <Link
                className="text-2xl text-gray-500"
                icon={IconID.ARROW_RIGHT}
                text={t('PRODUCT_OVERVIEW.GOTO_PRODUCT', { ns: 'category/productTeaser' })}
                bigIcon={false}
                href={currentLinkTarget}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
